import api from './api';

/**
 * Send one chat turn and read the SSE reply.
 *
 * The hands-free voice surface needs the reply token-by-token (so it can start
 * speaking on the first sentence instead of after the last one), which the
 * state-bound useChat hook doesn't expose. This is the bare transport: raw
 * fetch, because EventSource can't POST and axios can't stream.
 *
 * onChunk(text)  — a token of assistant text
 * onDone(id)     — the reply is complete; id is the saved message id
 * onPoints(data) — the point-extraction frames that follow `done`
 *
 * Resolves with the full reply text.
 */
export async function streamTurn(
  conversationId,
  content,
  { signal, onChunk, onDone, onPoints } = {}
) {
  const baseURL = process.env.REACT_APP_API_URL || '/api';
  const url = `${baseURL}/conversations/${conversationId}/stream/`;

  const open = () =>
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ content }),
      signal,
    });

  let response = await open();
  // This raw fetch bypasses the axios 401->refresh interceptor in lib/api.js,
  // so a stale access token would silently kill the stream.
  if (response.status === 401) {
    try {
      await api.post('/auth/refresh/');
      response = await open();
    } catch {
      throw new Error('Session expired');
    }
  }
  if (!response.ok || !response.body) {
    throw new Error(`Stream failed (${response.status})`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let full = '';

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';
    for (const line of lines) {
      if (!line.startsWith('data: ')) continue;
      let data;
      try {
        data = JSON.parse(line.slice(6));
      } catch {
        continue; // skip malformed JSON
      }
      // Guard the type so a points-only frame carrying content:"" can't be
      // mistaken for a text chunk.
      if (typeof data.content === 'string' && data.content) {
        full += data.content;
        onChunk?.(data.content);
      }
      if (data.done) onDone?.(data.message_id);
      if (data.points || data.removed_point_ids) onPoints?.(data);
      if (data.error) throw new Error(data.error);
    }
  }

  return full;
}

/** Upload one clip to the server-side transcriber. Returns the text (may be ''). */
export async function transcribeClip(blob) {
  const type = blob.type || 'audio/webm';
  const ext = type.includes('mp4') ? 'mp4' : type.includes('ogg') ? 'ogg' : 'webm';
  const form = new FormData();
  form.append('audio', blob, `clip.${ext}`);
  // Clear the JSON default so the browser sets multipart/form-data with its
  // boundary; otherwise the server can't find the field and returns 400.
  const res = await api.post('/transcribe/', form, {
    timeout: 120000,
    headers: { 'Content-Type': undefined },
  });
  return (res.data?.text || '').trim();
}
